/**
 * Auth prompt — shown when no valid token exists.
 * Validates the token against the health endpoint before persisting.
 */

import { useState, useCallback } from 'preact/hooks';
import { api, sanitizeToken } from '../api/client';
import { login } from './store';

export function AuthPrompt() {
  const [token, setToken] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = useCallback(
    async (e: Event) => {
      e.preventDefault();
      const trimmed = token.trim();
      if (!trimmed) return;

      // A mobile paste can carry a smart quote or a zero-width space. Those are dropped
      // before the token ever reaches a header, but say so on failure: otherwise a mangled
      // paste is indistinguishable from a genuinely wrong token.
      const cleaned = sanitizeToken(trimmed);
      const wasMangled = cleaned !== trimmed;

      setError('');
      setLoading(true);

      try {
        // Temporarily set the token so the health request uses it
        api.setToken(cleaned);
        await api.health();
        // Health check passed — persist
        login(cleaned);
      } catch (err) {
        // Revert the token so the prompt stays visible
        api.setToken('');
        setError(
          wasMangled
            ? 'That paste contained hidden characters and did not work. Retype the token by hand, or paste it into a plain notes app first and copy it back.'
            : err instanceof Error ? err.message : 'Invalid token. Please try again.',
        );
      } finally {
        setLoading(false);
      }
    },
    [token],
  );

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.logo}>J</div>
        <h2 style={styles.title}>Jarvis Console</h2>
        <p style={styles.subtitle}>Enter your access token to continue.</p>

        <form onSubmit={handleSubmit} style={styles.form}>
          <input
            type="password"
            value={token}
            onInput={(e) => setToken((e.target as HTMLInputElement).value)}
            placeholder="Access token"
            autoFocus
            style={styles.input}
            disabled={loading}
          />

          {error && <div style={styles.error}>{error}</div>}

          <button
            type="submit"
            disabled={loading || !token.trim()}
            style={{
              ...styles.button,
              ...(loading || !token.trim() ? styles.buttonDisabled : {}),
            }}
          >
            {loading ? 'Verifying...' : 'Log In'}
          </button>
        </form>
      </div>
    </div>
  );
}

/* ---------- Inline styles (small one-off component) ---------- */

const styles: Record<string, Record<string, string | number>> = {
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100dvh',
    background: 'var(--bg-primary)',
    padding: '24px',
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    maxWidth: '360px',
    padding: '40px 24px',
    background: 'var(--bg-secondary)',
    borderRadius: 'var(--radius)',
    border: '1px solid var(--border)',
  },
  logo: {
    width: '56px',
    height: '56px',
    borderRadius: '50%',
    background: 'var(--accent)',
    color: 'white',
    fontSize: '28px',
    fontWeight: '700',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '16px',
  },
  title: {
    fontSize: '20px',
    fontWeight: '700',
    color: 'var(--text-primary)',
    marginBottom: '4px',
  },
  subtitle: {
    fontSize: '14px',
    color: 'var(--text-secondary)',
    marginBottom: '24px',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    width: '100%',
  },
  input: {
    width: '100%',
    padding: '12px 16px',
    borderRadius: 'var(--radius-sm)',
    border: '1px solid var(--border)',
    background: 'var(--bg-tertiary)',
    color: 'var(--text-primary)',
    fontSize: '15px',
    outline: 'none',
  },
  error: {
    color: 'var(--danger)',
    fontSize: '13px',
    textAlign: 'center',
  },
  button: {
    padding: '12px',
    borderRadius: 'var(--radius-sm)',
    border: 'none',
    background: 'var(--accent)',
    color: 'white',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  buttonDisabled: {
    opacity: '0.5',
    cursor: 'default',
  },
};
